import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader } from "../../components/admin/AdminUI";
import DateInput from "../../components/DateInput";
import { formatDmy } from "../../utils/dateFormat";

// Halaman admin video kelas susulan.
//
// Susunannya sengaja RINGKAS: satu baris per sesi yang hanya menampilkan
// keadaannya (ada video atau belum, sampai kapan, berapa yang menonton).
// Form dan rinciannya baru terbuka saat admin menekan Manage / Add video, dan
// hanya satu baris yang terbuka pada satu waktu. Versi sebelumnya menampilkan
// form lengkap + lima tombol di kesepuluh sesi sekaligus, dan itu membuat
// halamannya sulit dibaca.
//
// Video di-key ke (kelas, Day, Sesi), BUKAN ke batch: admin hanya menyimpan
// rekaman batch terbaru dan menggantinya setelah kelas tahun berikutnya
// selesai. Karena itu pilihannya hanya kelas, tanpa batch.

const TEAL = "#0d9488";

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: TEAL });

const toast = (title) =>
  Swal.fire({ toast: true, position: "top-end", icon: "success", title, showConfirmButton: false, timer: 2000 });

const cardStyle = {
  backgroundColor: "white",
  border: "1px solid var(--adm-border)",
  borderRadius: "12px",
  padding: "20px 22px",
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
  marginBottom: "20px",
};
const labelStyle = { display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 600, color: "#374151" };
const hintStyle = { margin: "4px 0 0", fontSize: "12px", color: "#888" };
const tealButton = { backgroundColor: TEAL, color: "white" };

const durationText = (seconds) => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const sizeText = (bytes) => {
  if (!bytes) return "";
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
};

const slotId = (s) => `${s.dayNumber}-${s.sessionNumber}`;

const streamUrl = (videoId) => `${axios.defaults.baseURL || ""}/makeupvideos/${videoId}/stream`;

const Pill = ({ color, background, children }) => (
  <span
    style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      color,
      backgroundColor: background,
    }}
  >
    {children}
  </span>
);

const PILL = {
  green: { color: "#1d6b48", background: "#e2f3ea" },
  red: { color: "#a3261a", background: "#fde5e1" },
  amber: { color: "#8a5a0b", background: "#fdf3dc" },
  gray: { color: "#6b6770", background: "#f0efed" },
};

const videoStatus = (video, today) => {
  if (!video) return { label: "No video yet", tone: PILL.gray };
  if (!video.durationSeconds) return { label: "Duration unknown", tone: PILL.amber };
  if (!video.isActive) return { label: "Hidden from students", tone: PILL.gray };
  if (today > video.availableUntil) return { label: `Expired ${formatDmy(video.availableUntil)}`, tone: PILL.red };
  return { label: `Until ${formatDmy(video.availableUntil)}`, tone: PILL.green };
};

// Dropbox tidak selalu memberikan durasi video. Kalau kosong, browser admin
// yang membacanya dari file videonya sendiri (cukup metadatanya, bukan seluruh
// file) lalu menyimpannya. Tanpa durasi, video belum tampil ke student karena
// ambang tonton dihitung dari durasi. `onFailed` membuat form menampilkan
// kolom durasi manual.
const DurationProbe = ({ videoId, onSaved, onFailed }) => {
  const save = async (e) => {
    const seconds = e.currentTarget.duration;
    if (!Number.isFinite(seconds) || seconds <= 0) return onFailed();
    try {
      await axios.patch(`/makeupvideos/${videoId}/duration`, { durationSeconds: seconds });
      onSaved();
    } catch (error) {
      onFailed();
    }
  };
  return (
    <video
      src={streamUrl(videoId)}
      preload="metadata"
      muted
      style={{ display: "none" }}
      onLoadedMetadata={save}
      onError={onFailed}
    />
  );
};

const ACCESS_STATUS = {
  pending: { label: "Waiting for approval", tone: PILL.amber },
  approved: { label: "Approved", tone: PILL.green },
  rejected: { label: "Declined", tone: PILL.red },
};

// Kehadiran peminta di sesi yang dimintanya (batch terbaru kelas itu).
const ATTENDANCE = {
  absent: { label: "Absent", tone: PILL.red },
  attended: { label: "Attended", tone: PILL.green },
  makeup: { label: "Hadir Susulan", tone: PILL.green },
};

// Satu permintaan kelas susulan. Dipakai di kartu "Catchup requests" (semua
// kelas, `withSession`) dan di tab per video.
const AccessRow = ({ x, today, date, busy, withSession, onDate, onApprove, onReject, onRemove }) => {
  const status = ACCESS_STATUS[x.status] || ACCESS_STATUS.pending;
  // Tanpa tanggal khusus, student itu ikut batas umum video.
  const until = x.availableUntil || x.generalUntil;
  const lapsed = x.status === "approved" && today && until && today > until;
  const attendance = ATTENDANCE[x.attendance];
  return (
    <div className="row g-2 align-items-center" style={{ padding: "12px 0", borderTop: "1px solid #eceeee" }}>
      <div className="col-12 col-lg-6">
        <div style={{ fontSize: "14px", fontWeight: 600 }}>
          {x.name} <span style={{ fontWeight: 400, fontSize: "12px", color: "#888" }}>{x.studentId || x.studentEmail}</span>
        </div>
        <div style={{ fontSize: "12px", color: "#555" }}>
          {withSession && (
            <>
              {x.classCode} · Day {x.dayNumber} Sesi {x.sessionNumber}
            </>
          )}
          {x.batch && (
            <>
              {withSession ? " · " : ""}Batch {x.batch}
              {x.sessionDate && <> · {formatDmy(x.sessionDate)}</>}
            </>
          )}
          {attendance && (
            <span style={{ marginLeft: "6px" }}>
              <Pill {...attendance.tone}>{attendance.label}</Pill>
            </span>
          )}
        </div>
        {x.reason && (
          <div style={{ marginTop: "2px", fontSize: "12px", color: "#555", overflowWrap: "anywhere" }}>
            <span style={{ fontWeight: 600 }}>Reason:</span> &ldquo;{x.reason}&rdquo;
          </div>
        )}
        <div style={{ marginTop: "4px" }}>
          <Pill {...(lapsed ? PILL.red : status.tone)}>
            {x.status === "approved" ? `${lapsed ? "Expired" : "Approved until"} ${formatDmy(until)}` : status.label}
          </Pill>
          <span style={{ marginLeft: "8px", fontSize: "12px", color: "#888" }}>requested {formatDmy(x.requestedAt)}</span>
        </div>
      </div>
      <div className="col-12 col-lg-6">
        <div className="d-flex align-items-end flex-wrap gap-2 justify-content-lg-end">
          <div style={{ width: "190px" }}>
            <span style={{ ...labelStyle, fontWeight: 400 }}>Longer access until (optional)</span>
            <DateInput className="form-control form-control-sm" value={date} onChange={(e) => onDate(e.target.value)} />
          </div>
          <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={busy} onClick={onApprove}>
            {x.status === "approved" ? "Update date" : "Approve"}
          </button>
          {x.status === "pending" ? (
            <button className="adm-btn adm-btn--danger adm-btn--sm" disabled={busy} onClick={onReject}>
              Decline
            </button>
          ) : (
            <button className="adm-btn adm-btn--secondary adm-btn--sm" disabled={busy} onClick={onRemove}>
              {x.status === "approved" ? "Revoke" : "Allow to request again"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const weekdayDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(
        new Date(`${iso}T00:00:00Z`),
      )
    : "";

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

// Permintaan yang menunggu dikelompokkan per STUDENT (kapan pun ia
// mengirimnya), lalu per kelas di dalamnya. Urut dari student yang paling
// lama menunggu.
const byDaySession = (a, b) => a.dayNumber - b.dayNumber || a.sessionNumber - b.sessionNumber;

const groupRequests = (requests) => {
  const students = new Map();
  for (const x of requests) {
    if (!students.has(x.studentEmail)) students.set(x.studentEmail, { key: x.studentEmail, first: x, items: [] });
    students.get(x.studentEmail).items.push(x);
  }
  return [...students.values()]
    .map((g) => {
      const classes = new Map();
      for (const x of g.items) {
        if (!classes.has(x.classCode)) classes.set(x.classCode, { classCode: x.classCode, first: x, items: [] });
        classes.get(x.classCode).items.push(x);
      }
      const requestedAt = g.items.reduce((min, x) => (x.requestedAt < min ? x.requestedAt : min), g.items[0].requestedAt);
      return {
        ...g,
        requestedAt,
        classes: [...classes.values()]
          .map((c) => ({ ...c, items: c.items.sort(byDaySession) }))
          .sort((a, b) => a.first.className.localeCompare(b.first.className)),
      };
    })
    .sort((a, b) => (a.requestedAt < b.requestedAt ? -1 : 1));
};

// Pencarian: Student ID, nama, atau email (tidak peka huruf besar/kecil).
const matchesSearch = (x, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [x.studentId, x.name, x.studentEmail].some((v) => String(v || "").toLowerCase().includes(q));
};

const sectionLabel = {
  margin: "0 0 6px",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#8a8f98",
};

const REQUEST_ATTENDANCE_TEXT = {
  absent: "Absent in this session",
  attended: "Attended this session",
  makeup: "Already Hadir Susulan",
};

const ReasonBox = ({ reason }) => (
  <div
    style={{
      padding: "8px 12px",
      borderLeft: "3px solid #d1d5db",
      backgroundColor: "#fafafa",
      borderRadius: "4px",
      fontSize: "13px",
      color: reason ? "#1f2937" : "#9ca3af",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    }}
  >
    {reason || "No reason given."}
  </div>
);

// Satu sesi yang diminta, di dalam kartu RequestGroup. `showReason`: alasan
// sesi ini berbeda dari sesi lain di kelas yang sama, jadi ditulis di sini.
const RequestSessionRow = ({ x, today, date, busy, showReason, onDate, onApprove, onReject }) => {
  const [showDate, setShowDate] = useState(Boolean(date));
  const attendance = ATTENDANCE[x.attendance];
  const videoExpired = today && x.generalUntil && today > x.generalUntil;
  return (
    <div style={{ padding: "12px 16px", borderTop: "1px solid #eceeee" }}>
      <div className="d-flex flex-wrap align-items-center gap-3">
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>
            Day {x.dayNumber} · Sesi {x.sessionNumber}
          </div>
          <div style={{ fontSize: "13px", color: "#4b5563" }}>
            {x.sessionDate ? weekdayDate(x.sessionDate) : "Date unknown"}
            {x.durationSeconds ? <span style={{ color: "#9ca3af" }}> · video {durationText(x.durationSeconds)}</span> : null}
          </div>
        </div>

        <div style={{ flex: "1 1 220px", fontSize: "12px", color: "#4b5563" }}>
          <div style={{ marginBottom: "4px" }}>
            {attendance ? (
              <Pill {...attendance.tone}>{REQUEST_ATTENDANCE_TEXT[x.attendance]}</Pill>
            ) : (
              <Pill {...PILL.gray}>Attendance not recorded</Pill>
            )}
          </div>
          {videoExpired ? (
            <span style={{ color: "#a3261a" }}>
              Video expired on {formatDmy(x.generalUntil)} — set a longer access date to approve.
            </span>
          ) : (
            <span>Video available until {formatDmy(x.generalUntil)}</span>
          )}
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
          {(showDate || videoExpired) && (
            <div style={{ width: "160px" }}>
              <DateInput className="form-control form-control-sm" value={date} onChange={(e) => onDate(e.target.value)} />
            </div>
          )}
          <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={busy} onClick={onApprove}>
            <i className="bi bi-check-lg me-1"></i>Approve
          </button>
          <button className="adm-btn adm-btn--danger adm-btn--sm" disabled={busy} onClick={onReject}>
            <i className="bi bi-x-lg me-1"></i>Decline
          </button>
        </div>
      </div>

      {showReason && (
        <div style={{ marginTop: "10px" }}>
          <p style={sectionLabel}>Reason for missing this session</p>
          <ReasonBox reason={x.reason} />
        </div>
      )}

      {!showDate && !videoExpired && (
        <button
          type="button"
          className="btn btn-link p-0 mt-2"
          style={{ fontSize: "12px", color: "#6b7280", textDecoration: "none" }}
          onClick={() => setShowDate(true)}
        >
          <i className="bi bi-calendar-plus me-1"></i>Give this student longer access than {formatDmy(x.generalUntil)}
        </button>
      )}
    </div>
  );
};

// Semua permintaan SATU student yang masih menunggu, dikelompokkan per kelas.
// Awalnya tertutup: hanya student, kelas, dan daftar singkat sesinya. Klik
// bagian atas kartu (atau panahnya) untuk membuka rincian tiap sesi, alasan,
// dan tombol Approve/Decline per sesi.
const RequestGroup = ({ group, rowProps, busy, onApproveAll, onRejectAll }) => {
  const [open, setOpen] = useState(false);
  const { first, classes } = group;
  const total = group.items.length;
  const toggle = () => setOpen((o) => !o);
  // Tombol di bagian atas kartu tidak ikut membuka/menutup kartu.
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden", marginTop: "14px" }}>
      <div
        className="d-flex flex-wrap align-items-center gap-3"
        style={{ padding: "14px 16px", backgroundColor: "#fafafa", borderBottom: "1px solid #eceeee", cursor: "pointer" }}
        onClick={toggle}
      >
        <span
          aria-hidden="true"
          style={{
            flex: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#ccfbf1",
            color: "#0f766e",
            fontWeight: 700,
            fontSize: "14px",
          }}
        >
          {initials(first.name)}
        </span>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{first.name}</div>
          <div style={{ fontSize: "12px", color: "#6b7280", overflowWrap: "anywhere" }}>
            {first.studentId ? `Student ID ${first.studentId} · ` : ""}
            {first.studentEmail}
          </div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>
            <i className="bi bi-clock me-1"></i>
            {total} session{total === 1 ? "" : "s"} requested · waiting since {formatDmy(group.requestedAt)}
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {total > 1 && (
            <>
              <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={busy} onClick={stop(onApproveAll)}>
                <i className="bi bi-check2-all me-1"></i>Approve all {total}
              </button>
              <button className="adm-btn adm-btn--secondary adm-btn--sm" disabled={busy} onClick={stop(onRejectAll)}>
                Decline all
              </button>
            </>
          )}
          <button
            type="button"
            className="adm-btn adm-btn--secondary adm-btn--sm"
            aria-expanded={open}
            onClick={stop(toggle)}
          >
            {open ? "Hide details" : "View details"}
            <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ms-1`}></i>
          </button>
        </div>
      </div>

      {classes.map((c) => {
        // Satu alasan untuk semua sesi kelas ini: ditulis sekali di atas.
        const reasons = new Set(c.items.map((x) => x.reason || ""));
        const sharedReason = reasons.size === 1;
        return (
          <div key={c.classCode}>
            <div style={{ padding: "12px 16px 10px" }}>
              <div className="row g-3">
                <div className={sharedReason ? "col-12 col-md-5" : "col-12"}>
                  <p style={sectionLabel}>Class</p>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>{c.first.className}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {c.classCode}
                    {c.first.batch ? ` · Batch ${c.first.batch}` : ""} · {c.items.length} session
                    {c.items.length === 1 ? "" : "s"}
                  </div>
                  {/* Saat tertutup: daftar singkat sesi yang diminta. */}
                  {!open && (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {c.items.map((x) => (
                        <span
                          key={x.id}
                          style={{
                            padding: "3px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#374151",
                            backgroundColor: "white",
                          }}
                        >
                          Day {x.dayNumber} · Sesi {x.sessionNumber}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {sharedReason && (
                  <div className="col-12 col-md-7">
                    <p style={sectionLabel}>Reason for missing class</p>
                    <ReasonBox reason={c.first.reason} />
                  </div>
                )}
              </div>
            </div>
            {open &&
              c.items.map((x) => <RequestSessionRow key={x.id} {...rowProps(x)} showReason={!sharedReason} />)}
          </div>
        );
      })}
    </div>
  );
};

const CatchupVideosPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);

  // Kode kelas yang sudah punya jadwal, dan kelas yang sedang dibuka.
  const [classCodes, setClassCodes] = useState([]);
  const [classCode, setClassCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Baris sesi yang sedang dibuka, tab-nya, dan isian form-nya. Hanya satu
  // baris yang terbuka, jadi cukup satu draft.
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("settings");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewers, setViewers] = useState(null);
  // id video yang durasinya gagal dideteksi otomatis.
  const [probeFailed, setProbeFailed] = useState({});

  // Permintaan izin yang masih menunggu, dari semua kelas. Barisnya lahir dari
  // permintaan student di portal; admin hanya menyetujui atau menolak.
  const [requests, setRequests] = useState([]);
  const [requestsToday, setRequestsToday] = useState("");
  // Tanggal yang sedang diketik admin, di-key ke id permintaan.
  const [decisionDates, setDecisionDates] = useState({});
  const [decidingId, setDecidingId] = useState(null);
  // Pencarian di kartu "Catchup requests": Student ID, nama, atau email.
  const [requestSearch, setRequestSearch] = useState("");
  const requestGroups = groupRequests(requests);
  const shownGroups = requestGroups.filter((g) => matchesSearch(g.first, requestSearch));

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchClassCodes = async () => {
    try {
      const res = await axios.get("/classsessions/batches");
      setClassCodes([...new Set((res.data || []).map((b) => b.classCode))]);
    } catch (error) {
      setClassCodes([]);
    }
  };

  const fetchVideos = async () => {
    if (!classCode) return;
    setLoading(true);
    try {
      const res = await axios.get("/makeupvideos", { params: { classCode } });
      setData(res.data);
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to load the catchup videos.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get("/makeupvideos/requests");
      setRequests(res.data.requests || []);
      setRequestsToday(res.data.today || "");
    } catch (error) {
      setRequests([]);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    fetchClassCodes();
    fetchRequests();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  useEffect(() => {
    setData(null);
    setOpenId(null);
    fetchVideos();
  }, [classCode]);

  const fetchViewers = async (video) => {
    setViewers({ videoId: video.id, rows: null });
    try {
      const res = await axios.get(`/makeupvideos/${video.id}/progress`);
      setViewers({ videoId: video.id, rows: res.data.viewers });
    } catch (error) {
      setViewers({ videoId: video.id, rows: [] });
      swalError(error.response?.data?.msg || "Failed to load the viewers.");
    }
  };

  const selectTab = (session, nextTab) => {
    setTab(nextTab);
    if (nextTab === "viewers" && session.video) fetchViewers(session.video);
  };

  const openRow = (session, nextTab = "settings") => {
    setOpenId(slotId(session));
    setDraft({
      source: session.video?.dropboxLink || "",
      availableUntil: session.video?.availableUntil || "",
      durationMinutes: "",
      isActive: session.video ? session.video.isActive : true,
    });
    selectTab(session, session.video ? nextTab : "settings");
  };

  const handleSave = async (session) => {
    if (!draft.source.trim() || !draft.availableUntil) {
      return swalError("Isi link Dropbox dan tanggal 'Available until' terlebih dahulu.");
    }
    setSaving(true);
    try {
      await axios.put(`/makeupvideos/slot/${encodeURIComponent(classCode)}/${session.dayNumber}/${session.sessionNumber}`, {
        source: draft.source,
        availableUntil: draft.availableUntil,
        durationMinutes: draft.durationMinutes,
        isActive: draft.isActive,
      });
      await fetchVideos();
      setOpenId(null);
      toast("Video saved");
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to save the catchup video.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (session) => {
    const { video } = session;
    const lines = [`Video kelas susulan Day ${session.dayNumber} Sesi ${session.sessionNumber} akan dihapus.`];
    if (video.viewers > 0) lines.push(`Progres tonton ${video.viewers} student ikut terhapus.`);
    lines.push("Absensi yang sudah diperbaiki lewat video ini TIDAK berubah. File di Dropbox tidak dihapus.");

    const result = await Swal.fire({
      title: "Hapus video ini?",
      html: lines.map((l) => `<p style="margin:0 0 8px;font-size:14px">${l}</p>`).join(""),
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      cancelButtonText: "Batal",
      confirmButtonColor: "#d33",
      confirmButtonText: "Ya, hapus",
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`/makeupvideos/${video.id}`);
      setOpenId(null);
      fetchVideos();
      fetchRequests();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to remove the catchup video.");
    }
  };

  const refreshAccess = () => {
    fetchRequests();
    fetchVideos();
  };

  // Tanggal di kolom: ketikan admin, atau tanggal izin yang sudah berlaku.
  const decisionDate = (x) => decisionDates[x.id] ?? x.availableUntil ?? "";

  // Tanggal kosong = student itu ikut batas umum video.
  const handleApprove = async (x) => {
    const availableUntil = decisionDate(x) || null;
    setDecidingId(x.id);
    try {
      await axios.patch(`/makeupvideos/extensions/${x.id}/approve`, { availableUntil });
      setDecisionDates((prev) => ({ ...prev, [x.id]: undefined }));
      refreshAccess();
      toast(`Request approved for ${x.name}`);
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to approve the request.");
    } finally {
      setDecidingId(null);
    }
  };

  const handleReject = async (x) => {
    setDecidingId(x.id);
    try {
      await axios.patch(`/makeupvideos/extensions/${x.id}/reject`);
      refreshAccess();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to decline the request.");
    } finally {
      setDecidingId(null);
    }
  };

  const handleRemoveAccess = async (x) => {
    setDecidingId(x.id);
    try {
      await axios.delete(`/makeupvideos/extensions/${x.id}`);
      refreshAccess();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to remove the request.");
    } finally {
      setDecidingId(null);
    }
  };

  // Menyetujui/menolak semua sesi satu kiriman, satu per satu. Yang gagal
  // (mis. videonya sudah expired tanpa tanggal khusus) dilaporkan per sesi.
  const handleGroup = async (group, approve) => {
    setDecidingId(group.key);
    const failed = [];
    for (const x of group.items) {
      try {
        if (approve) {
          await axios.patch(`/makeupvideos/extensions/${x.id}/approve`, { availableUntil: decisionDate(x) || null });
        } else {
          await axios.patch(`/makeupvideos/extensions/${x.id}/reject`);
        }
      } catch (error) {
        failed.push(`Day ${x.dayNumber} Sesi ${x.sessionNumber}: ${error.response?.data?.msg || "failed"}`);
      }
    }
    setDecidingId(null);
    refreshAccess();
    if (failed.length) swalError(failed.join(" — "));
    else toast(`${approve ? "Approved" : "Declined"} ${group.items.length} sessions for ${group.first.name}`);
  };

  const accessRowProps = (x) => ({
    x,
    today: data?.today || requestsToday,
    date: decisionDate(x),
    busy: decidingId === x.id,
    onDate: (value) => setDecisionDates((prev) => ({ ...prev, [x.id]: value })),
    onApprove: () => handleApprove(x),
    onReject: () => handleReject(x),
    onRemove: () => handleRemoveAccess(x),
  });

  const sessions = data?.sessions || [];
  const withVideo = sessions.filter((s) => s.video).length;

  // ── isi baris yang sedang dibuka ──

  const renderSettings = (session) => {
    const { video } = session;
    const needsDuration = Boolean(video && !video.durationSeconds && probeFailed[video.id]);
    return (
      <>
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <label style={labelStyle}>Dropbox link of the video</label>
            <input
              className="form-control"
              value={draft.source}
              onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              placeholder="https://www.dropbox.com/scl/fi/..."
            />
            <p style={hintStyle}>
              Di Dropbox: klik Share pada file videonya, lalu Copy link. Link-nya boleh tetap tertutup (request access).
            </p>
          </div>
          <div className="col-12 col-sm-6 col-lg-4">
            <label style={labelStyle}>Available until</label>
            <DateInput
              className="form-control"
              value={draft.availableUntil}
              onChange={(e) => setDraft({ ...draft, availableUntil: e.target.value })}
            />
            <p style={hintStyle}>Hari terakhir student boleh menonton video ini.</p>
          </div>
          {needsDuration && (
            <div className="col-12 col-sm-6 col-lg-4">
              <label style={labelStyle}>Video length (minutes)</label>
              <input
                className="form-control"
                value={draft.durationMinutes}
                onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value.replace(/[^\d.]/g, "") })}
                placeholder="e.g. 95"
              />
              <p style={{ ...hintStyle, color: "#a3261a" }}>
                Durasi tidak bisa dideteksi otomatis. Isi di sini — tanpa durasi, video belum tampil ke student.
              </p>
            </div>
          )}
        </div>

        {/* Checkbox polos, bukan form-check/form-switch Bootstrap: gaya global
            situs ini mengubah tampilannya menjadi lingkaran merah. */}
        <label className="d-inline-flex align-items-center gap-2 mt-3" style={{ fontSize: "14px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            style={{ width: "18px", height: "18px", margin: 0, accentColor: TEAL, cursor: "pointer" }}
          />
          Show this video to students
        </label>

        <div className="d-flex align-items-center flex-wrap gap-2 mt-3">
          <button className="adm-btn adm-btn--primary" disabled={saving} onClick={() => handleSave(session)}>
            {saving ? "Saving..." : video ? "Save changes" : "Add video"}
          </button>
          <button className="adm-btn adm-btn--secondary" disabled={saving} onClick={() => setOpenId(null)}>
            Cancel
          </button>
          {video && (
            <button className="adm-btn adm-btn--danger ms-auto" onClick={() => handleRemove(session)}>
              <i className="bi bi-trash-fill me-1"></i> Remove video
            </button>
          )}
        </div>
      </>
    );
  };

  const renderViewers = (video) => {
    if (!viewers || viewers.videoId !== video.id || viewers.rows === null) {
      return <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>Loading...</p>;
    }
    if (viewers.rows.length === 0) {
      return <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>No student has opened this video yet.</p>;
    }
    return (
      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm align-middle" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th>Student</th>
              <th>Batch</th>
              <th>Watched</th>
              <th>Status</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {viewers.rows.map((v) => (
              <tr key={v.studentEmail}>
                <td style={{ fontSize: "13px" }}>
                  {v.name}
                  <span style={{ color: "#888", fontSize: "12px" }}> — {v.studentId || v.studentEmail}</span>
                </td>
                <td style={{ fontSize: "13px" }}>{v.batch || "—"}</td>
                <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                  {v.percent}% <span style={{ color: "#888" }}>({durationText(v.watchedSeconds)})</span>
                </td>
                <td>
                  {v.completedAt ? (
                    <Pill {...PILL.green}>Hadir Susulan · {formatDmy(v.completedAt)}</Pill>
                  ) : (
                    <Pill {...PILL.amber}>In progress</Pill>
                  )}
                </td>
                <td style={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>{formatDmy(v.lastHeartbeatAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRequests = (video) =>
    video.extensions.length === 0 ? (
      <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>
        No student has requested this catchup class yet. Students send a request from the Catchup Class page of the
        portal.
      </p>
    ) : (
      video.extensions.map((x) => <AccessRow key={x.id} {...accessRowProps(x)} />)
    );

  const renderOpenRow = (session) => {
    const { video } = session;
    const pendingCount = video ? video.extensions.filter((x) => x.status === "pending").length : 0;
    const tabs = video
      ? [
          { key: "settings", label: "Video settings" },
          { key: "preview", label: "Preview" },
          { key: "viewers", label: `Viewers (${video.viewers})` },
          { key: "requests", label: `Requests (${video.extensions.length})`, alert: pendingCount > 0 },
        ]
      : [];
    return (
      <div style={{ padding: "16px 18px 18px", borderRadius: "8px", backgroundColor: "#f6f8f8" }}>
        {tabs.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`adm-tab${tab === t.key ? " adm-tab--active" : ""}`}
                onClick={() => selectTab(session, t.key)}
              >
                {t.label}
                {t.alert && tab !== t.key && (
                  <span
                    style={{ display: "inline-block", width: "8px", height: "8px", marginLeft: "6px", borderRadius: "50%", backgroundColor: "#e0a800" }}
                  ></span>
                )}
              </button>
            ))}
          </div>
        )}

        {tab === "settings" && renderSettings(session)}
        {video && tab === "preview" && (
          <video
            src={streamUrl(video.id)}
            controls
            preload="metadata"
            style={{ display: "block", width: "100%", maxWidth: "760px", borderRadius: "8px", backgroundColor: "black" }}
          />
        )}
        {video && tab === "viewers" && renderViewers(video)}
        {video && tab === "requests" && renderRequests(video)}
      </div>
    );
  };

  return (
    <div>
      <div className="adm-main">
        <PageHeader
          title="Catchup Videos"
          subtitle={
            <>
          Students request a catchup class in the Student Portal (session and reason); the recording appears for
          them once you approve the request. After watching {data?.thresholdPercent ?? 80}% of the recording of a
          session they missed, their attendance becomes &ldquo;Hadir Susulan&rdquo;. Each session has one video, for
          students of the class&rsquo;s newest batch only — replace it with the next batch&rsquo;s recording once
          that class has run.
            </>
          }
        />

        {data && !data.dropboxConfigured && (
          <div className="alert alert-warning" style={{ fontSize: "13px" }}>
            Dropbox belum dikonfigurasi di server (DROPBOX_APP_KEY, DROPBOX_APP_SECRET, DROPBOX_REFRESH_TOKEN di file
            .env backend). Video baru belum bisa ditambahkan dan video yang ada belum bisa diputar.
          </div>
        )}

        {requests.length > 0 && (
          <div style={{ ...cardStyle, borderLeft: "4px solid #e0a800" }}>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <i className="bi bi-bell-fill" style={{ color: "#e0a800" }}></i>
              <span style={{ fontWeight: 700, fontSize: "17px" }}>Catchup requests</span>
              <Pill {...PILL.amber}>
                {requestGroups.length} student{requestGroups.length === 1 ? "" : "s"} · {requests.length} session
                {requests.length === 1 ? "" : "s"} waiting
              </Pill>
            </div>
            <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#666" }}>
              Baca alasan student, lalu Approve atau Decline setiap sesi. Sesi yang di-approve muncul di portal student
              dan bisa ditonton sampai tanggal &ldquo;Video available until&rdquo;. Beri akses lebih lama hanya kalau
              student itu memang butuh waktu tambahan.
            </p>

            <div className="position-relative mt-3" style={{ maxWidth: "420px" }}>
              <i
                className="bi bi-search"
                aria-hidden="true"
                style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}
              ></i>
              <input
                type="search"
                className="form-control"
                style={{ paddingLeft: "36px" }}
                placeholder="Search by student ID, name, or email"
                aria-label="Search catchup requests by student ID, name, or email"
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
              />
            </div>

            {shownGroups.length === 0 ? (
              <p style={{ margin: "16px 0 0", fontSize: "13px", color: "#888" }}>
                No request matches &ldquo;{requestSearch.trim()}&rdquo;.
              </p>
            ) : (
              shownGroups.map((g) => (
                <RequestGroup
                  key={g.key}
                  group={g}
                  busy={decidingId === g.key}
                  rowProps={(x) => ({ ...accessRowProps(x), busy: decidingId === x.id || decidingId === g.key })}
                  onApproveAll={() => handleGroup(g, true)}
                  onRejectAll={() => handleGroup(g, false)}
                />
              ))
            )}
          </div>
        )}

        <div style={cardStyle}>
          <div className="d-flex align-items-end flex-wrap gap-3">
            <div style={{ flex: "0 1 280px" }}>
              <label style={labelStyle}>Class</label>
              <select className="form-select" value={classCode} onChange={(e) => setClassCode(e.target.value)}>
                <option value="">Select...</option>
                {classCodes.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            {data && (
              <p style={{ margin: "0 0 8px", fontSize: "13px", color: "#555" }}>
                <b>
                  {withVideo} of {sessions.length}
                </b>{" "}
                sessions have a catchup video.{" "}
                {data.currentBatch ? (
                  <>
                    Shown to students of <b>Batch {data.currentBatch}</b>.
                  </>
                ) : (
                  <span style={{ color: "#a3261a" }}>No batch of this class has started yet.</span>
                )}
              </p>
            )}
          </div>
          {classCodes.length === 0 && (
            <p style={hintStyle}>No class schedule has been generated yet. Generate one on the Class Sessions page first.</p>
          )}

          {!classCode ? (
            <p style={{ margin: "20px 0 0", fontSize: "14px", color: "#888" }}>
              Select a class to see its sessions.
            </p>
          ) : loading && !data ? (
            <p style={{ margin: "20px 0 0", color: "#888" }}>Loading...</p>
          ) : (
            <div style={{ marginTop: "18px" }}>
              {/* Judul kolom hanya di layar lebar; di layar sempit tiap baris
                  menumpuk dan isinya sudah menjelaskan dirinya sendiri. */}
              <div
                className="row g-2 d-none d-lg-flex"
                style={{ padding: "0 4px 8px", fontSize: "12px", fontWeight: 600, color: "#888" }}
              >
                <div className="col-lg-2">Session</div>
                <div className="col-lg-4">Video</div>
                <div className="col-lg-2">Available</div>
                <div className="col-lg-2">Students</div>
                <div className="col-lg-2"></div>
              </div>

              {sessions.map((s) => {
                const { video } = s;
                const status = videoStatus(video, data.today);
                const isOpen = openId === slotId(s);
                const pendingCount = video ? video.extensions.filter((x) => x.status === "pending").length : 0;
                return (
                  <div key={slotId(s)} style={{ borderTop: "1px solid #eceeee", padding: "12px 4px" }}>
                    <div className="row g-2 align-items-center">
                      <div className="col-12 col-lg-2">
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>
                          Day {s.dayNumber} · Sesi {s.sessionNumber}
                        </div>
                      </div>

                      <div className="col-12 col-lg-4" style={{ minWidth: 0 }}>
                        {video ? (
                          <>
                            <div style={{ fontSize: "13px", overflowWrap: "anywhere" }}>{video.fileName}</div>
                            <div style={{ fontSize: "12px", color: "#888" }}>
                              {[sizeText(video.fileSize), video.durationSeconds ? durationText(video.durationSeconds) : ""]
                                .filter(Boolean)
                                .join(" · ")}
                              {!video.durationSeconds && !probeFailed[video.id] && (
                                <>
                                  {" "}
                                  Detecting length...
                                  <DurationProbe
                                    videoId={video.id}
                                    onSaved={fetchVideos}
                                    onFailed={() => setProbeFailed((prev) => ({ ...prev, [video.id]: true }))}
                                  />
                                </>
                              )}
                              {!video.durationSeconds && probeFailed[video.id] && (
                                <span style={{ color: "#a3261a" }}> Length not detected — open Manage to fill it in.</span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span style={{ fontSize: "13px", color: "#aaa" }}>—</span>
                        )}
                      </div>

                      <div className="col-6 col-lg-2">
                        <Pill {...status.tone}>{status.label}</Pill>
                      </div>

                      <div className="col-6 col-lg-2" style={{ fontSize: "13px" }}>
                        {video ? (
                          <>
                            <div>
                              {video.completed} completed
                              <span style={{ color: "#888" }}> of {video.viewers} who opened it</span>
                            </div>
                            {pendingCount > 0 && (
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                style={{ fontSize: "12px", fontWeight: 600, color: "#8a5a0b", textDecoration: "none" }}
                                onClick={() => openRow(s, "requests")}
                              >
                                <i className="bi bi-bell-fill me-1"></i>
                                {pendingCount} request{pendingCount === 1 ? "" : "s"}
                              </button>
                            )}
                          </>
                        ) : (
                          <span style={{ color: "#aaa" }}>—</span>
                        )}
                      </div>

                      <div className="col-12 col-lg-2 text-lg-end">
                        {isOpen ? (
                          <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => setOpenId(null)}>
                            Close <i className="bi bi-chevron-up ms-1"></i>
                          </button>
                        ) : video ? (
                          <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => openRow(s)}>
                            Manage <i className="bi bi-chevron-down ms-1"></i>
                          </button>
                        ) : (
                          <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => openRow(s)}>
                            <i className="bi bi-plus-lg me-1"></i> Add video
                          </button>
                        )}
                      </div>
                    </div>

                    {isOpen && draft && <div style={{ marginTop: "12px" }}>{renderOpenRow(s)}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatchupVideosPage;
